import opConfig from 'openpathConfig';
import { t } from 'i18next';

import { getAccessToken } from './accessToken';
import { setUnifiedCookie } from './setUnifiedCookie';
import {
  isNetworkFetchError,
  getNetworkErrorMessage,
} from './networkErrorHelpers';

/** Used for local development against a remote deployment url. Running the `proxy-server` will allow api calls to make it through successfully */
const { AWARE_PROXY_URL } = opConfig;

type AwareClient = {
  [K in keyof OP.Aware.Client]: (
    params: Parameters<OP.Aware.Client[K]>[0],
  ) => Promise<Awaited<ReturnType<OP.Aware.Client[K]>> | null>;
};

interface AwareRequest<Body extends object = object> {
  url: string;
  method?: 'PUT' | 'GET' | 'PATCH' | 'POST' | 'DELETE';
  body?: Body;
}

export interface AwareRequestError {
  suppressErrorMessage: boolean;
  status?: number;
}

interface RequestParams {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  credentials: 'include';
  headers: Record<string, string>;
  body?: string;
}

const awareRequest = async <
  ResponseBody = unknown,
  RequestBody extends object = object,
>({
  url,
  method = 'GET',
  body,
}: AwareRequest<RequestBody>): Promise<ResponseBody | null> => {
  const accessToken = getAccessToken();
  const params: RequestParams = {
    method,
    credentials: 'include', // Needed to send the Aware cookie with the request for authentication
    headers: {},
  };

  params.headers = {
    ...params.headers,
    Authorization: `Bearer ${accessToken}`,
  };

  if (body) {
    params.body = JSON.stringify(body);
    params.headers = {
      ...params.headers,
      'Content-Type': 'application/json',
    };
  }

  const handleFetchError = (err: unknown): never => {
    if (isNetworkFetchError(err)) {
      const msg = getNetworkErrorMessage();
      throw Object.assign(new Error(msg), { localizedMessage: msg });
    }
    throw err;
  };

  let response: Response | undefined;
  try {
    response = await fetch(url, params);
  } catch (err) {
    handleFetchError(err);
  }
  if (response === undefined) {
    throw new Error(
      t(
        "Your request couldn't be completed. Please check your connection and try again.",
      ),
    );
  }

  // Lets suppress this for now (in QueryClient in App.js)
  // If we want to show a message just add it to the object below and remove suppressErrorMessage
  const errorObject: AwareRequestError = {
    suppressErrorMessage: true,
  };
  if (!response.ok) {
    // Try set a cookie and try once more (attempting legacy auth)
    // need to remove the Bearer token before we retry
    delete params.headers?.Authorization;
    try {
      await setUnifiedCookie({ baseUrl: url });
      response = await fetch(url, params);
    } catch (err) {
      handleFetchError(err);
    }

    if (!response.ok) {
      errorObject.status = response.status;
      throw errorObject;
    }
  }

  // Check if the response body is empty. Fetch may omit Content-Length for
  // 204 No Content, so check the status as well.
  if (
    response.status === 204 ||
    response.headers.get('content-length') === '0'
  ) {
    return null;
  }

  // Try to parse the response body as JSON
  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error('Failed to parse JSON:', err);
    throw err;
  }

  return data as ResponseBody;
};

/**
 * @description Main client utility for making api calls to Aware. This is being build on a as
 * needed basis thus should be extended as use cases increase. An example of this being used can
 * be found within useAwareQuery which is used within the PermissionedAwareDeepLink component. The utility
 * is typed so that when you need specific parameters for example in describeDevicePermission you will
 * get proper Typescript help to know when you need them and what they are
 */
export const awareClient: AwareClient = {
  // ===========================================================================
  // Access Control Servers
  // ===========================================================================

  listAccessControlServers: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest<OP.Aware.AccessControlServer[]>({
      url: `${finalUrl}/api/v1/accessControlServers`,
    });
  },

  describeAccessControlServer: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [id] = parameters;

    return awareRequest<OP.Aware.AccessControlServer>({
      url: `${finalUrl}/api/v1/accessControlServers/${id}`,
    });
  },

  // ===========================================================================
  // Video-owned label restrictions for Helium policy sections
  // ===========================================================================

  // Admin MVP follow-up: confirm the Aware /api/v1/admin/* endpoints accept a
  // policy-scoped JWT before the policies epic ships
  listAdminLabels: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [filters] = parameters ?? [];

    const queryParams = new URLSearchParams();
    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    const queryPart = queryString ? `?${queryString}` : '';
    const url = `${finalUrl}/api/v1/admin/labels${queryPart}`;

    return awareRequest<OP.Aware.ListAdminLabelsResponse>({ url });
  },

  getAdminPolicySectionLabels: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [policySectionId] = parameters;

    return awareRequest<OP.Aware.AdminPolicySectionLabelsResponse>({
      url: `${finalUrl}/api/v1/admin/policySections/${policySectionId}/labels`,
    });
  },

  setAdminPolicySectionLabels: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [policySectionId, body] = parameters;

    return awareRequest<OP.Aware.AdminPolicySectionLabelsResponse>({
      url: `${finalUrl}/api/v1/admin/policySections/${policySectionId}/labels`,
      method: 'PUT',
      body,
    });
  },

  deleteAdminPolicySectionLabels: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [policySectionId] = parameters;

    await awareRequest({
      url: `${finalUrl}/api/v1/admin/policySections/${policySectionId}/labels`,
      method: 'DELETE',
    });
  },

  // ===========================================================================
  // Access Control Points
  // ===========================================================================

  listAccessControlPoints: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest<OP.Aware.AccessControlPoint[]>({
      url: `${finalUrl}/api/v1/accessControlPoints`,
    });
  },

  describeAccessControlPoint: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [guid] = parameters;

    return awareRequest<OP.Aware.AccessControlPoint>({
      url: `${finalUrl}/api/v1/accessControlPoints/${guid}`,
    });
  },

  createAccessControlPoints: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const body = parameters?.[0];

    return awareRequest<OP.Aware.AccessControlPoint[]>({
      url: `${finalUrl}/api/v1/accessControlPoints`,
      method: 'POST',
      body,
    });
  },

  // ===========================================================================
  // Cameras
  // ===========================================================================

  getClaimableCamera: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [serial] = parameters;

    return awareRequest({
      url: `${finalUrl}/api/v1/cloud/claimableCamera?serial=${encodeURIComponent(serial)}`,
    });
  },

  // ===========================================================================
  // Config
  // ===========================================================================

  describeConfig: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/public/config` });
  },

  // ===========================================================================
  // Device Audio Outputs
  // ===========================================================================

  listDeviceAudioOutputs: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest<OP.Aware.DeviceAudioOutput[]>({
      url: `${finalUrl}/api/v1/deviceAudioOutputs`,
    });
  },

  updateDeviceAudioOutput: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [deviceAudioOutputId, body] = parameters;

    return awareRequest<OP.Aware.DeviceAudioOutput>({
      url: `${finalUrl}/api/v1/deviceAudioOutputs/${deviceAudioOutputId}`,
      method: 'PUT',
      body,
    });
  },

  // ===========================================================================
  // Device Configs
  // ===========================================================================

  listDeviceConfigs: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/deviceConfigs` });
  },

  describeDeviceConfig: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [id] = parameters;

    return awareRequest<OP.Aware.DeviceConfig>({
      url: `${finalUrl}/api/v1/deviceConfigs/${id}`,
    });
  },

  createDeviceConfig: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [body] = parameters;

    return awareRequest<OP.Aware.CreateDeviceConfigPayload>({
      url: `${finalUrl}/api/v1/deviceConfigs`,
      method: 'POST',
      body,
    });
  },

  // ===========================================================================
  // Device Groups
  // ===========================================================================

  listDeviceGroups: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/deviceGroups` });
  },

  describeDeviceGroup: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [id] = parameters;

    return awareRequest<OP.Aware.DeviceGroup>({
      url: `${finalUrl}/api/v1/deviceGroups/${id}`,
    });
  },

  createDeviceGroup: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [body] = parameters;

    return awareRequest<OP.Aware.CreateDeviceGroupPayload>({
      url: `${finalUrl}/api/v1/deviceGroups`,
      method: 'POST',
      body,
    });
  },

  // ===========================================================================
  // Device Sites
  // ===========================================================================

  listDeviceSites: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/deviceSites` });
  },

  describeDeviceSite: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [id] = parameters;

    return awareRequest<OP.Aware.DeviceSite>({
      url: `${finalUrl}/api/v1/deviceSites/${id}`,
    });
  },

  // ===========================================================================
  // Remote Storage
  // ===========================================================================

  getRemoteStorageStatus: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [siteId, deviceId] = parameters;

    return awareRequest<OP.Aware.RemoteStorageStatus>({
      url: `${finalUrl}/api/v1/sites/${siteId}/devices/${deviceId}/remoteStorageStatus`,
    });
  },

  configureRemoteStorage: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [siteId, deviceId, body] = parameters;

    return awareRequest<OP.Aware.RemoteStorageConfigurePayload>({
      url: `${finalUrl}/api/v1/sites/${siteId}/devices/${deviceId}/remoteStorage/configure`,
      method: 'POST',
      body,
    });
  },

  getRemoteStorageConfigure: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [siteId, deviceId] = parameters;

    return awareRequest({
      url: `${finalUrl}/api/v1/sites/${siteId}/devices/${deviceId}/remoteStorage/configure`,
    });
  },

  // ===========================================================================
  // Devices
  // ===========================================================================

  describeDevice: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [guid] = parameters;

    return awareRequest<OP.Aware.Device>({
      url: `${finalUrl}/api/v1/devices/${guid}`,
    });
  },

  listDevices: async ({
    deploymentUrl,
    parameters,
  }: OP.Aware.ListDevicesParams) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const filter = parameters?.[0] ?? {};

    const queryParams = new URLSearchParams();
    if (filter.device_group_id) {
      queryParams.set('eq:device_group_id', filter.device_group_id);
    }

    const queryString = queryParams.toString();
    const queryPart = queryString ? `?${queryString}` : '';
    const url = `${finalUrl}/api/v1/devices${queryPart}`;

    return awareRequest({ url });
  },

  createDevices: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [_siteId, options, body] = parameters;

    const queryParams = new URLSearchParams();
    if (options.sync !== undefined) {
      queryParams.set('sync', String(options.sync));
    }
    if (options.add_if_comm_fails !== undefined) {
      queryParams.set('add_if_comm_fails', String(options.add_if_comm_fails));
    }

    const queryString = queryParams.toString();
    const queryPart = queryString ? `?${queryString}` : '';
    const url = `${finalUrl}/api/v1/devices${queryPart}`;

    return awareRequest<OP.Aware.CreateDevicesResponse>({
      url,
      method: 'POST',
      body,
    });
  },

  updateDevice: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [deviceId, body] = parameters;

    return awareRequest<OP.Aware.UpdateDevicePayload>({
      url: `${finalUrl}/api/v1/devices/${deviceId}`,
      method: 'PUT',
      body,
    });
  },

  // ===========================================================================
  // Permission Labels
  // ===========================================================================

  listPermissionLabels: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/permissionLabels` });
  },

  createPermissionLabel: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [body] = parameters;

    return awareRequest<OP.Aware.PermissionLabel>({
      url: `${finalUrl}/api/v1/permissionLabels`,
      method: 'POST',
      body,
    });
  },

  // ===========================================================================
  // Permissions
  // ===========================================================================

  describeDevicePermission: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({
      url: `${finalUrl}/api/v1/permissions/${parameters?.deviceId}`,
    });
  },

  // ===========================================================================
  // Rules
  // ===========================================================================

  listRules: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({
      url: `${finalUrl}/api/v1/rules3`,
    });
  },

  listRulesPermissions: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({
      url: `${finalUrl}/api/v1/rules3Permissions`,
    });
  },

  updateRule: async ({ deploymentUrl, parameters }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;
    const [awareRuleId, body] = parameters;

    return awareRequest<(typeof parameters)[1]>({
      url: `${finalUrl}/api/v1/rules3/${awareRuleId}`, // The uuid of the rule
      method: 'PUT',
      body,
    });
  },

  // ===========================================================================
  // Servers
  // ===========================================================================

  listServers: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/servers` });
  },

  // ===========================================================================
  // Statistics
  // ===========================================================================

  getProtectStatistics: ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/statistics/protect` });
  },

  // ===========================================================================
  // Users
  // ===========================================================================

  describeUser: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/me` });
  },

  listUserGroups: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({ url: `${finalUrl}/api/v1/userGroups` });
  },

  lookupUser: async ({ deploymentUrl }) => {
    const normalizedUrl = deploymentUrl.replace(/\/$/, '');
    const finalUrl = AWARE_PROXY_URL || normalizedUrl;

    return awareRequest({
      url: `${finalUrl}/api/v1/public/lookupUser`,
    });
  },
};
